import { useState, type ComponentType, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LogOut, Menu, UserRound, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { fileUrl } from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { version } from '../../package.json';

export interface SidebarNavItem {
  key: string;
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  /** Only highlight on exact match (e.g. the Home/index item) */
  end?: boolean;
}

interface AppShellProps {
  /** Shell title shown in the sidebar header (kept as text for a11y/tests) */
  title: string;
  items: readonly SidebarNavItem[];
  profileTo: string;
  /** Extra content for the content top bar (e.g. the admin TimerWidget) */
  topRight?: ReactNode;
  children: ReactNode;
}

function initialsOf(name: string | undefined, email: string | undefined): string {
  const source = name?.trim() || email || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function SidebarUserCard({ profileTo }: { profileTo: string }) {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const fullName = (user as { fullName?: string }).fullName;

  return (
    <div onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-white/10 transition-colors"
          >
            <Avatar className="h-8 w-8">
              {user.avatarPath && <AvatarImage src={fileUrl(user.avatarPath)} alt="" />}
              <AvatarFallback className="bg-white/20 text-white text-xs">
                {initialsOf(fullName, user.email)}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-white truncate">
                {fullName ?? user.email}
              </span>
              {fullName && (
                <span className="block text-xs text-white/50 truncate">{user.email}</span>
              )}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="end" className="w-52">
          <DropdownMenuLabel className="font-normal">
            <span className="block text-sm font-medium truncate">{fullName ?? user.email}</span>
            {fullName && (
              <span className="block text-xs text-muted-foreground truncate">{user.email}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={profileTo}>
              <UserRound className="h-4 w-4 mr-2" />
              Your profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              void signOut();
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Ventin brand colors (ventinmedia.com): primary blue + dark navy */
const BRAND_BLUE = 'bg-[#1924a2]';
const BRAND_NAVY = 'bg-[#101540]';

function SidebarContent({ title, items, profileTo }: Omit<AppShellProps, 'children' | 'topRight'>) {
  const { signOut } = useAuth();
  const isAdmin = /admin/i.test(title);
  return (
    <>
      <div className={cn('flex items-center gap-2 px-4 h-14', BRAND_BLUE)}>
        <img src="/ventin-logo-white.png" alt="Ventin" className="h-7 w-auto" />
        <span className="sr-only">{title}</span>
        {isAdmin && (
          <span className="ml-auto rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            Admin
          </span>
        )}
      </div>

      <nav className={cn('flex-1 overflow-y-auto px-2 py-3', BRAND_NAVY)}>
        <ul className="space-y-0.5">
          {items.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-white transition-colors',
                    isActive ? 'bg-white/15' : 'hover:bg-white/10',
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className={cn('px-2 py-2 flex items-center gap-1', BRAND_BLUE)}>
        <div className="flex-1 min-w-0">
          <SidebarUserCard profileTo={profileTo} />
        </div>
        <button
          type="button"
          aria-label="Sign out"
          title="Sign out"
          onClick={() => {
            void signOut();
          }}
          className="p-2 rounded-md text-white/60 hover:bg-white/10 hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      <div
        className={cn(
          'px-4 py-2.5 border-t border-white/15 flex items-center justify-between text-xs font-semibold text-white',
          BRAND_NAVY,
        )}
      >
        <span>Version {version}</span>
        <Link to="changelog" className="hover:underline transition-colors">
          View Changelog
        </Link>
      </div>
    </>
  );
}

/**
 * AppShell — sidebar navigation shell shared by the admin and portal areas.
 * Ventin navy sidebar: logo header, nav items, user card with hover flyout,
 * version + changelog at the very bottom. Collapses to a hamburger drawer
 * below md.
 */
export function AppShell({ title, items, profileTo, topRight, children }: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-muted">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-30 w-60 flex-col bg-[#101540]">
        <SidebarContent title={title} items={items} profileTo={profileTo} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 w-60 flex flex-col bg-[#101540]">
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="absolute right-2 top-3.5 z-10 p-1.5 text-white/70 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <div onClick={() => setMobileOpen(false)}>
              <SidebarContent title={title} items={items} profileTo={profileTo} />
            </div>
          </aside>
        </div>
      )}

      <div className="md:pl-60 flex min-h-screen flex-col">
        {/* Top bar: hamburger on mobile, optional extras (e.g. timer) on the right */}
        <header
          className={cn(
            'bg-background border-b',
            !topRight && 'md:hidden', // desktop top bar only needed when it has content
          )}
        >
          <div className="px-4 h-14 flex items-center gap-3">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-md hover:bg-muted"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img src="/favicon-32.png" alt="" className="md:hidden h-6 w-6" />
            <div className="ml-auto flex items-center gap-3">{topRight}</div>
          </div>
        </header>

        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
